/**
 * Clinic-wide 施術録印刷 (Treatment Record Print) hooks.
 * Triggered from 施術録検索 (/treatment_records/init) after user selects rezepts.
 */

import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { DEFAULT_FETCH_ERROR_MESSAGE, getApiErrorMessage } from '@/utils/apiError';
import {
  initTreatmentRecordPrintClinicWide,
  createTreatmentRecordPrintClinicWidePreviewTicket,
  type TreatmentRecordPrintClinicWideTicketResult,
} from '@/services/main/treatmentRecord';
import { PDF_ERROR_MESSAGE } from '@/pages/main/treatmentRecord/constants';
import type {
  TreatmentRecordPrintClinicWideInitData,
  TreatmentRecordPrintClinicWidePayload,
} from '@/pages/main/treatmentRecord/types';

/**
 * Fetch init data (saved settings + visit_year_months) when modal opens.
 * Pass `null` to skip (modal closed).
 */
export const useTreatmentRecordPrintClinicWideInit = (rezeptIds: number[] | null) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const query = useQuery<TreatmentRecordPrintClinicWideInitData, Error>({
    queryKey: [
      'treatment-record-print-clinic-wide',
      'init',
      tenant,
      clinicNumber,
      serviceType,
      rezeptIds,
    ],
    queryFn: () =>
      initTreatmentRecordPrintClinicWide(tenant, clinicNumber, rezeptIds ?? [], serviceType),
    enabled: !!tenant && !!clinicNumber && rezeptIds !== null && rezeptIds.length > 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.isError && query.error) {
      toast.error(getApiErrorMessage(query.error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [query.isError, query.error, toast]);

  return query;
};

/**
 * Build PDF (clinic-wide). Returns a preview URL — caller opens it in a new
 * tab so the browser picks up the server-provided filename for Save-As.
 * BE issues a short-lived ticket so the unauthenticated new-tab navigation
 * can still render the PDF.
 */
export const useTreatmentRecordPrintClinicWideOutput = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { mutateAsync: outputPdfMutation, isPending: isLoading } = useMutation<
    TreatmentRecordPrintClinicWideTicketResult,
    Error,
    TreatmentRecordPrintClinicWidePayload
  >({
    mutationFn: payload =>
      createTreatmentRecordPrintClinicWidePreviewTicket(tenant, clinicNumber, payload, serviceType),
    onError: error => {
      toast.error(getApiErrorMessage(error, PDF_ERROR_MESSAGE));
    },
  });

  return { outputPdfMutation, isLoading };
};

